/**
 * Camera adapter cho web — Prompt.md §10, §33.
 *
 * Đây là một trong ba chỗ duy nhất của app được gọi browser API trực tiếp (cùng với
 * vision provider và storage). Mọi tầng trên chỉ thấy `MediaStream` + `CameraError`.
 */

export type CameraFacing = "user" | "environment";

export type CameraErrorKind =
  "PERMISSION_DENIED" | "NO_DEVICE" | "IN_USE" | "INSECURE_CONTEXT" | "UNKNOWN";

export class CameraError extends Error {
  readonly kind: CameraErrorKind;

  constructor(kind: CameraErrorKind, message?: string) {
    super(message ?? kind);
    this.name = "CameraError";
    this.kind = kind;
  }
}

/**
 * Ánh xạ lỗi của `getUserMedia` sang taxonomy trên.
 * `NotReadableError`/`TrackStartError` (camera đang bị app khác giữ) rất hay xảy ra trên
 * Android khi trẻ vừa dùng camera của app khác, và thông báo phải nói đúng cách xử lý.
 */
function toCameraError(err: unknown): CameraError {
  if (err instanceof CameraError) return err;
  const name = err instanceof Error ? err.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return new CameraError("PERMISSION_DENIED");
    case "NotFoundError":
    case "OverconstrainedError":
      return new CameraError("NO_DEVICE");
    case "NotReadableError":
    case "TrackStartError":
      return new CameraError("IN_USE");
    default:
      return new CameraError("UNKNOWN", err instanceof Error ? err.message : String(err));
  }
}

export interface CameraStartOptions {
  facingMode: CameraFacing;
}

export interface CameraService {
  start(opts: CameraStartOptions): Promise<MediaStream>;
  /** Đổi camera trước/sau, giữ nguyên các listener đã đăng ký. */
  switchCamera(): Promise<MediaStream>;
  stop(): void;
  /**
   * iOS: track bị kết thúc khi tab ẩn / cuộc gọi đến (quyết định #14). Không có callback này,
   * app sẽ hiển thị frame đông cứng và vẫn "chấm điểm" tư thế trên frame chết.
   */
  onTrackEnded(cb: () => void): () => void;
  readonly facingMode: CameraFacing;
  readonly stream: MediaStream | null;
}

/**
 * `initialFacing` là camera đã lưu ở settings (§27).
 *
 * Nhận qua tham số chứ không đọc storage ở đây: adapter này không được biết đến IndexedDB, và
 * mỗi screen dựng một service riêng nên nếu service tự đọc thì sẽ có ba lần đọc bất đồng bộ
 * cho cùng một giá trị.
 */
export function createCameraService(initialFacing: CameraFacing = "user"): CameraService {
  let stream: MediaStream | null = null;
  let facingMode: CameraFacing = initialFacing;
  const endedListeners = new Set<() => void>();
  let detachTrackListeners: (() => void) | null = null;

  function attachTrackListeners(next: MediaStream): void {
    const handler = (): void => {
      for (const cb of endedListeners) cb();
    };
    const tracks = next.getVideoTracks();
    for (const track of tracks) track.addEventListener("ended", handler);
    detachTrackListeners = () => {
      for (const track of tracks) track.removeEventListener("ended", handler);
    };
  }

  function releaseStream(): void {
    detachTrackListeners?.();
    detachTrackListeners = null;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      stream = null;
    }
  }

  async function start(opts: CameraStartOptions): Promise<MediaStream> {
    // Phải kiểm tra TRƯỚC khi chạm vào navigator.mediaDevices: ở non-secure context thuộc
    // tính này là `undefined`, truy cập .getUserMedia sẽ throw TypeError và bị gộp vào
    // UNKNOWN — đúng cái bẫy làm dev tưởng là bug permission.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      throw new CameraError("INSECURE_CONTEXT");
    }

    releaseStream();
    facingMode = opts.facingMode;
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          // Không xin 1080p: §31 ưu tiên nhiệt độ và pin, và pose model chỉ cần ~256px.
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: false,
      });
      stream = next;
      attachTrackListeners(next);
      return next;
    } catch (err) {
      throw toCameraError(err);
    }
  }

  return {
    start,
    switchCamera: () => start({ facingMode: facingMode === "user" ? "environment" : "user" }),
    stop: releaseStream,
    onTrackEnded(cb) {
      endedListeners.add(cb);
      return () => endedListeners.delete(cb);
    },
    get facingMode() {
      return facingMode;
    },
    get stream() {
      return stream;
    },
  };
}
